import PropTypes from 'prop-types';
import React from 'react';
import { Form, Icon, SelectLabel, RadioLabel, Counter, CheckboxLabel, Tooltip, Numbers, Section, OptionalSection, Message, ToastContext, Card } from '@weezevent/nacre';

import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import RateLinker from './rateLinker';
import EventOnly from '../../components/eventOnly';
import RateFees from '../../components/rates/rate_fees/rateFees';
import EventSettings from '../../models/eventSettings';
import Styles from '../../styles/rate/rateCreation.module.css';
import { fetcher } from '@weezevent/weezjs-core';
import { compute_sum_float } from './utils';
import SelectTaxes from '../../components/tax/selectTaxes';
import { useSegment } from '../../components/tracking/trackingProvider';
import { capitalizeFirstLetter } from '../attendee/detail/components/utils';
import { RateNameInput, RateDescriptionInput } from './components/inputs/RateInputs';
import Total from '../../components/rates/total';
import SellingPrice from '../../components/rates/sellingPrice/selling-price';
export function Commission({ event, commission, currency, rateType, isTaxless = false }) {
    const { t } = useTranslation();

    return event ? (
        <Tooltip triggerOnHover={true} title={t('ticket.rates.infos.commissionTooltip')} className={Styles['rate-commission-tooltip']} position={'bottom'}>
            <div className={Styles['rate-commission']}>
                <span className={Styles['rate-commission-label']}>
                    {rateType === 'open' ? t('ticket.rates.infos.commissionMin') : t(`ticket.rates.infos.commission${isTaxless ? 'Taxless' : ''}`)}
                </span>
                <Numbers value={commission} locale={i18n.language} currency={currency} />
            </div>
        </Tooltip>
    ) : (
        <div className={Styles['rate-commission']}></div>
    );
}

Commission.propTypes = {
    rateType: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    commission: PropTypes.number.isRequired
};

function PriceContainer({
    event = null,
    form,
    onDebouncePriceChange,
    formErrors,
    rateLimit,
    loadingSettings,
    rate,
    settings,
    includeTaxInPrice,
    displayFees,
    onChangeForm,
    commission,
    computedPrice,
    commissionTaxless,
    addFees
}) {
    const { t } = useTranslation();

    if (event) {
        return (
            <Card className={Styles['price-container']}>
                <Card.Content>
                    <div className={Styles['infos-field-price-container']}>
                        <EventOnly event={event}>
                            {form?.type === 'fixed' && (
                                <div className={Styles['infos-field-price-more']} onClick={addFees}>
                                    <span>{t('ticket.rates.infos.fees.more')}</span>
                                    <span className={Styles['infos-field-price-more-icon']}>
                                        <Icon name="plus" />
                                    </span>
                                </div>
                            )}
                        </EventOnly>
                        {form.fees.length > 0 && (
                            <SellingPrice
                                fees={form.fees}
                                onDebouncePriceChange={onDebouncePriceChange}
                                formErrors={formErrors}
                                form={form}
                                event={event}
                                rateLimit={rateLimit}
                                loadingSettings={loadingSettings}
                                rate={rate}
                                settings={settings}
                                includeTaxInPrice={includeTaxInPrice}
                            />
                        )}
                    </div>
                    <EventOnly event={event}>
                        {displayFees && Boolean(form.fees?.length > 0) && (
                            <RateFees
                                fees={form.fees}
                                currency={event?.currency}
                                onChange={value => onChangeForm('fees', value)}
                                onChangeForm={onChangeForm}
                                displayExclVat={!loadingSettings && settings.values.aff_ht === '1'}
                                includeTaxInPrice={includeTaxInPrice}
                            />
                        )}
                        {form?.type !== 'free' && (
                            <Total
                                currency={event?.currency}
                                fees={form.fees}
                                type={form.type}
                                price={form.price ? form.price : rate.rate_template?.price}
                                disabled={form.price === null}
                                displayExclVat={!loadingSettings && settings.values.aff_ht === '1'}
                                includeTaxInPrice={includeTaxInPrice}
                                event={event}
                                form={form}
                                computedPrice={computedPrice}
                                commission={commission}
                                commissionTaxless={commissionTaxless}
                                onPriceChange={onDebouncePriceChange}
                                onDebouncePriceChange={onDebouncePriceChange}
                                formErrors={formErrors}
                                settings={settings}
                                rate={rate}
                                rateLimit={rateLimit}
                                loadingSettings={loadingSettings}
                            />
                        )}
                    </EventOnly>
                </Card.Content>
            </Card>
        );
    }
    return (
        <SellingPrice
            fees={form.fees}
            onDebouncePriceChange={onDebouncePriceChange}
            formErrors={formErrors}
            form={form}
            event={event}
            rateLimit={rateLimit}
            loadingSettings={loadingSettings}
            rate={rate}
            settings={settings}
            includeTaxInPrice={includeTaxInPrice}
        />
    );
}
export default function RateFormInfos({
    organizationId,
    defaultLanguage,
    languageOptions,
    event = null,
    form,
    onChangeForm,
    onChangeFormObject,
    formErrors,
    rateTemplates = [],
    groups = [],
    forms = [],
    rate,
    rateTypes,
    rateLimit,
    handleRateLinker,
    taxes = [],
    includeTaxInPrice = () => {},
    taxToUse = null
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);
    const [commission, setCommission] = React.useState(null);
    const timeoutPriceChange = React.useRef(null);

    const [cachePrice, setCachePrice] = React.useState(form.price || 0);
    const [cacheFees, setCacheFees] = React.useState(form.fees || []);
    const [settings, loadingSettings] = EventSettings.useApiModel(
        {
            organizationId: organizationId,
            eventId: event?.pk,
            launch: Boolean(event)
        },
        [event, organizationId]
    );

    const _onChangeForm = React.useCallback(
        (key, value) => {
            // we save edited values as states because between rate types, some datas are not present
            // ("free" doesnt have price/fees) ("open" doesnt have fees)
            if (key === 'name') setNameModified(true);
            if (key === 'price') setCachePrice(value);
            if (key === 'fees' && form.type === 'fixed') setCacheFees(value);
            onChangeForm(key, value);
        },
        [onChangeForm, form]
    );

    const [nameModified, setNameModified] = React.useState(rate?.id);

    const mapToGroups = React.useCallback(group => ({ key: group.pk, text: group.name ?? group?.group_template?.name, value: group.pk }), []);
    const optionsGroups = React.useMemo(() => {
        //Preselect no groups
        let firstOption = [
            {
                key: 'no_group',
                text: t('ticket.rates.infos.noGroups'),
                value: 'no_group'
            }
        ];

        return firstOption.concat(groups.map(mapToGroups));
    }, [groups, mapToGroups]);

    const mapToForms = React.useCallback(form => ({ key: form.weez_id, text: form.name, value: form.weez_id }), []);
    const optionsForms = React.useMemo(() => {
        //Preselect no forms
        let firstOption = [
            {
                key: 'no_form',
                text: t('ticket.rates.infos.noForms'),
                value: 'no_form'
            }
        ];

        return firstOption.concat(forms.map(mapToForms));
    }, [forms, mapToForms]);

    const onChangeUnlimitedQuantity = React.useCallback(
        (e, { value, checked }) => {
            if (!checked) {
                onChangeForm('quantity', 0);
            } else {
                onChangeForm('quantity', null);
            }
        },
        [onChangeForm]
    );

    const displayFees = React.useMemo(() => {
        // Fees interactions are displayed if
        // We are in an event rate, with fixed price non herited from a template
        if (form.type !== 'fixed' || !event) {
            return false;
        }
        if (!form.price && rate?.rate_template?.price) {
            return false;
        }
        return true;
    }, [form, rate]);

    const totalFees = React.useMemo(() => {
        let total = 0;
        if (form.price && form.type === 'fixed' && form.fees?.length > 0) {
            total = form.fees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(amount) || 0), 0);
        }

        return total;
    }, [form.fees, form.type]);

    // const totalFeesTaxless = React.useMemo(() => {
    //     let total = 0;
    //     if (form.price && form.type === 'fixed' && form.fees?.length > 0) {
    //         total = form.fees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(includeTaxInPrice(false, amount)) || 0), 0);
    //     }

    //     return total;
    // }, [form.fees, form.type]);

    /* commission calculation */
    const computedPrice = React.useMemo(() => {
        return form.price === null ? rate?.rate_template.price : compute_sum_float(+form.price, totalFees);
    }, [form.price, rate, totalFees]);

    // const computedPriceTaxless = React.useMemo(() => {
    //     return form.price === null ? includeTaxInPrice(false, rate?.rate_template.price) : compute_sum_float(includeTaxInPrice(false, +form.price), totalFeesTaxless);
    // }, [form.price, rate, totalFeesTaxless]);

    const commissionTaxless = React.useMemo(() => includeTaxInPrice(false, commission), [commission, taxToUse]);
    //Query
    const getCommission = React.useCallback(
        (amount, mode = 'after_com') => {
            if (!amount || !event?.id) {
                return;
            }
            let queryParams = { price: parseFloat(amount).toFixed(2) };
            if (rate?.id) {
                queryParams['rate'] = rate?.id;
            }

            if (mode === 'after_com') {
                queryParams['from_final_price'] = true;
            }

            fetcher
                .get(`/ticket/organizations/${organizationId}/events/${event.id}/rates/commission`, { query: queryParams })
                .then(response => {
                    let _commission = response.response.commission;
                    setCommission(response.response.commission);

                    if (mode === 'before_com') {
                        let price = amount - totalFees + _commission;
                        if (price) {
                            price = Math.round(price * 100) / 100;
                        }
                        _onChangeForm('price', price);
                    }
                })
                .catch(e => {
                    toast.error(t(`ticket.rates.infos.commissionError`));
                });
        },
        [rate, event, organizationId, totalFees, _onChangeForm]
    );

    const handleLanguage = React.useCallback(
        (value, name) => {
            let _form = { ...form };
            let translations = _form[`translations${capitalizeFirstLetter(name)}`];

            // Set default value upon language selection
            if (!(value in translations)) {
                translations[value] = '';
            }

            // Delete empty keys
            Object.keys(translations)
                .filter(k => k !== value)
                .forEach(key => {
                    if (translations[key] === '' && translations[key] !== null) {
                        delete translations[key];
                    }
                });
            _form[`translations${name.toLowerCase()}`] = translations;
            onChangeFormObject({ ..._form }, true);
        },
        [form, onChangeFormObject]
    );

    const cleanTranslations = React.useCallback(
        trans => {
            let transToApply = { ...trans };
            if (
                Object.keys(trans).length > 1 &&
                Object.entries(trans)
                    .filter(k => k[0] !== defaultLanguage)
                    .every(key => key[1] === null)
            ) {
                transToApply[defaultLanguage] = null;
            }

            return transToApply;
        },
        [defaultLanguage]
    );

    const handleTranslations = React.useCallback(
        (value, property, selectedLanguage) => {
            // Set both name and description values, as well as translations
            if (property === 'name' && value.length >= 200) {
                // max length
                return;
            }
            let _form = { ...form };
            let trans = _form[`translations${capitalizeFirstLetter(property)}`];
            trans[selectedLanguage] = value;
            if (selectedLanguage !== defaultLanguage) {
                if (!value && property === 'name') {
                    trans[selectedLanguage] = null;
                }
            } else {
                _onChangeForm(property, value);
            }
            _onChangeForm(`translations${capitalizeFirstLetter(property)}`, cleanTranslations(trans));
        },
        [_onChangeForm, form, defaultLanguage, cleanTranslations]
    );

    //Loading
    React.useEffect(() => {
        getCommission(computedPrice);
    }, [getCommission, totalFees]); // eslint-disable-line
    /* end commission calculation */

    function onDebouncePriceChange(mode, tax) {
        return event => {
            if (timeoutPriceChange.current) {
                clearTimeout(timeoutPriceChange.current);
            }
            timeoutPriceChange.current = setTimeout(() => {
                onPriceChange(event, mode, tax);
            }, 500);

            return () => clearTimeout(timeoutPriceChange.current);
        };
    }

    //before_com => income for orga => commission excluded only
    function onPriceChange(event, mode, tax) {
        let price = 0;

        //rateTemplate linked from InheritedInput
        if (!event) {
            onChangeFormObject({ price: null, fees: [] }, true);
            return getCommission(rate.rate_template?.price + totalFees, 'after_com');
        }

        //rateTemplate unlinked from InheritedInput
        if (event.value === undefined) {
            price = rate.rate_template?.price;
            //default case
        } else {
            price = event.value;
        }

        // Every thing is stored TTC, so if tax == without-tax, then get the "with-tax" amount.
        if (tax === 'without-tax') {
            price = includeTaxInPrice(true, price);
        }

        if (mode === 'total-buyer') {
            // Need to remove fees to get initial price
            price = price - totalFees;
        }

        if (price) {
            price = Math.round(price * 100) / 100;
        }

        if (mode === 'total-orga') {
            getCommission(price, 'before_com');
        } else {
            _onChangeForm('price', price);
            getCommission(price + totalFees, 'after_com');
        }
    }

    const addFees = React.useCallback(() => {
        if (!form.fees.length) {
            onChangeForm('fees', [{ name: null, amount: 0, amount_no_tax: 0 }]);
            track('ACTION > DISPLAY FEES', { 'Page Title': 'NEW RATE', Open: displayFees });
        } else {
            const emptyFee = { name: null, amount: 0, amount_no_tax: 0 };
            return onChangeForm('fees', [...form.fees, emptyFee]);
        }
    }, [form, displayFees]);

    const onChangeType = React.useCallback(
        rate_type => {
            const rateInstance = { ...rate_type };
            // form.type is precedent type so if it was free we use cached price (since form.price == 0)
            const newPrice = form.type !== 'free' ? form.price : cachePrice;
            // if we need a price and a price was entered we overwrite rateInstance default price
            if (rate_type.type !== 'free' && newPrice > 0) rateInstance.price = newPrice;
            // if a name was entered we use it instead of rateInstance's
            if (nameModified) {
                rateInstance.name = form.name || rate?.rate_template?.name || '';
            }
            // we only use fees for "fixed" type
            if (rate_type.type !== 'fixed') {
                rateInstance.fees = [];
            } else {
                rateInstance.fees = cacheFees;
            }
            onChangeFormObject({ ...rateInstance }, true);
            getCommission(rateInstance.price);
            const segmentRateInfo = rate_type.type === 'fixed' ? 'Fixed' : rate_type.type === 'open' ? 'Donation' : 'Free';
            track('ACTION > SELECT RATE TYPE', { 'Page Title': 'NEW RATE', 'Rate info': segmentRateInfo });
        },
        [form, rate, cachePrice, cacheFees, nameModified, onChangeFormObject]
    );

    return (
        <>
            <EventOnly event={event}>
                {rateTemplates && !rate?.distributor && Boolean(rateTemplates.length) && (
                    <RateLinker rateTemplate={form?.rate_template_id} rateTemplates={rateTemplates} onChange={handleRateLinker} />
                )}
            </EventOnly>
            <Form.Field style={{ marginBottom: '0' }}>
                <Form.Label label={t('ticket.rates.infos.rateType')} />
                {Object.keys(rateTypes).map(key => {
                    const rate_type = rateTypes[key];
                    return (
                        <RadioLabel
                            disabled={!form.type}
                            className={Styles['radio-label-custom']}
                            key={rate_type.type}
                            inline
                            bordered
                            name={rate_type.type}
                            checked={form.type ? form.type === rate_type.type : rate?.rate_template?.type === rate_type.type}
                            label={rate_type.name}
                            onClick={() => onChangeType(rate_type)}
                            value={rate_type.type}
                        />
                    );
                })}
                {rate?.rate_template?.type && (
                    <Tooltip triggerOnHover title={<span>{form.type ? t('ticket.rate_template.link_field_template') : t('ticket.rate_template.unlink_field_template')}</span>}>
                        <Icon
                            className={Styles['inherit-label-icon']}
                            name={form.type ? 'unlink' : 'link'}
                            onClick={() => {
                                if (form.type) {
                                    onChangeForm('type', null);
                                } else {
                                    onChangeForm('type', rate?.rate_template?.type);
                                }
                            }}
                        />
                    </Tooltip>
                )}
            </Form.Field>
            <Section className={Styles['extra-fees-section']}>
                <div className={Styles['container-infos-name-price']}>
                    <Form.Field className={Styles['infos-field-name']}>
                        <RateNameInput
                            errors={formErrors['name'] ? true : false}
                            rate={rate}
                            form={form}
                            event={event}
                            defaultLanguage={defaultLanguage}
                            languageOptions={languageOptions}
                            setNameModified={setNameModified}
                            handleLanguage={handleLanguage}
                            onChangeFormObject={onChangeFormObject}
                            handleTranslations={handleTranslations}
                        />
                        <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : null} />
                    </Form.Field>
                    {form?.type !== 'free' && (
                        <PriceContainer
                            event={event}
                            form={form}
                            onDebouncePriceChange={onDebouncePriceChange}
                            formErrors={formErrors}
                            rateLimit={rateLimit}
                            loadingSettings={loadingSettings}
                            rate={rate}
                            settings={settings}
                            includeTaxInPrice={includeTaxInPrice}
                            displayFees={displayFees}
                            onChangeForm={_onChangeForm}
                            commission={commission}
                            computedPrice={computedPrice}
                            commissionTaxless={commissionTaxless}
                            addFees={addFees}
                        />
                    )}
                </div>
            </Section>
            {!rate?.distributor && (
                <>
                    <EventOnly event={event}>
                        <Form.Field>
                            <Form.Label label={t(`ticket.rates.infos.quantity`)} optionalText={t('ticket.common.labelOptional')} />
                            <div className={Styles['container-infos-quantity']}>
                                {event?.use_sessions ? (
                                    <Message type="default" title={t('ticket.rates.infos.session.title')}>
                                        {t('ticket.rates.infos.session.message')}
                                    </Message>
                                ) : (
                                    <div className={Styles['container-infos-quantity']}>
                                        <Counter
                                            errors={formErrors['quantity'] ? true : false}
                                            placeholder={0}
                                            onChange={(event, value) => onChangeForm('quantity', value)}
                                            disabled={form.quantity === null}
                                            value={form.quantity}
                                            min={0}
                                        />
                                        <CheckboxLabel
                                            onChange={onChangeUnlimitedQuantity}
                                            checked={form.quantity === null}
                                            label={t(`ticket.rates.infos.unlimitedQuantity`)}
                                            name={'unlimited_quantity'}
                                        />
                                    </div>
                                )}
                            </div>
                            <Form.FieldErrors errors={formErrors['quantity'] ? [t(formErrors['quantity'])] : null} />
                        </Form.Field>
                        <Form.Field style={{ marginBottom: '0' }}>
                            <Form.Label label={t(`ticket.rates.infos.disableLabel`)} />
                            <div>
                                <RadioLabel
                                    className={Styles['radio-label-custom']}
                                    inline
                                    bordered
                                    name={'unable'}
                                    checked={!form.disabled}
                                    label={
                                        <span>
                                            {t(`ticket.rates.infos.unable`)} <Icon name="eye" />
                                        </span>
                                    }
                                    onClick={() => {
                                        onChangeForm('disabled', false);
                                        track('ACTION > ONLINE VISIBILITY', { 'Page Title': 'NEW RATE', 'Visibility info': 'Online' });
                                    }}
                                />
                                <RadioLabel
                                    className={Styles['radio-label-custom']}
                                    inline
                                    bordered
                                    name={'disable'}
                                    checked={form.disabled}
                                    label={
                                        <span>
                                            {t(`ticket.rates.infos.disable`)} <Icon name="eye-slash" />
                                        </span>
                                    }
                                    onClick={() => {
                                        onChangeForm('disabled', true);
                                        track('ACTION > ONLINE VISIBILITY', { 'Page Title': 'NEW RATE', 'Visibility info': 'Hide' });
                                    }}
                                />
                            </div>
                        </Form.Field>
                        <SelectTaxes
                            label={t('ticket.rates.infos.tax')}
                            taxes={taxes}
                            onClick={() => {
                                track('ACTION > SELECT TAX RATE', { 'Page Title': 'NEW RATE' });
                            }}
                            onChange={(optionValue, option) => onChangeForm('tax_id', optionValue === 'event' ? null : optionValue)}
                            value={form.tax_id === null ? 'event' : form.tax_id}
                            withOptionTaxEvent
                            errors={formErrors['tax_id'] ? [t(formErrors['tax_id'])] : null}
                        />
                    </EventOnly>
                    {groups.length ? (
                        <Form.Field>
                            <SelectLabel
                                value={form.group === null ? 'no_group' : form.group}
                                label={t(`ticket.rates.infos.group`)}
                                onClick={() => {
                                    track('ACTION > SELECT RATE GROUP', { 'Page Title': 'NEW RATE' });
                                }}
                                onChange={value => onChangeForm('group', value === 'no_group' ? null : value)}
                                options={optionsGroups}
                            />
                        </Form.Field>
                    ) : null}
                    <EventOnly event={event}>
                        {forms.length ? (
                            <Form.Field>
                                <SelectLabel
                                    value={form.form_id === null ? 'no_form' : form.form_id}
                                    label={t(`ticket.rates.infos.form`)}
                                    onClick={() => {
                                        track('ACTION > SELECT FORM', { 'Page Title': 'NEW RATE' });
                                    }}
                                    onChange={value => onChangeForm('form_id', value === 'no_form' ? null : value)}
                                    options={optionsForms}
                                />
                            </Form.Field>
                        ) : null}
                    </EventOnly>
                    <OptionalSection
                        onToggle={toggle => {
                            track('ACTION > DISPLAY DESCRIPTION', { 'Page Title': 'NEW RATE', Open: toggle });
                        }}
                        className={Styles['optional-section__description']}
                        open={Boolean(form?.description || (defaultLanguage in form.translationsDescription && Object.keys(form.translationsDescription).length > 1))}
                        label={
                            <div>
                                <span>{t('ticket.rates.infos.add_description')}</span>
                                <span className={Styles['optional']}>{t('ticket.rates.infos.optional')}</span>
                            </div>
                        }
                    >
                        <Form.Field className={Styles['infos-field-name']}>
                            <RateDescriptionInput
                                rate={rate}
                                form={form}
                                event={event}
                                handleLanguage={handleLanguage}
                                defaultLanguage={defaultLanguage}
                                languageOptions={languageOptions}
                                handleTranslations={handleTranslations}
                            />
                            <Form.FieldErrors errors={formErrors['description'] ? [t(formErrors['description'])] : null} />
                        </Form.Field>
                    </OptionalSection>
                </>
            )}
        </>
    );
}

RateFormInfos.propTypes = {
    organizationId: PropTypes.number.isRequired,
    event: PropTypes.object,
    form: PropTypes.object.isRequired,
    rate: PropTypes.object.isRequired,
    rateTypes: PropTypes.object.isRequired,
    groups: PropTypes.array,
    forms: PropTypes.array,
    rateTemplates: PropTypes.array,
    onChangeForm: PropTypes.func.isRequired,
    onChangeFormObject: PropTypes.func.isRequired,
    formErrors: PropTypes.object.isRequired,
    handleRateLinker: PropTypes.func.isRequired
};
